import { useVerifiedOnboarding } from "../onboard-verify/verified-onboarding-context.ts";
import { useCallback, useState } from "react";
import { CircleCheck } from "lucide-react";

import { Card } from "../../shared/card/card.tsx";
import { WizardRailLayout } from "../onboarding-shared/wizard-rail-layout.tsx";
import { ApiError } from "../../../utils/api.ts";
import { showError, showSuccess } from "../../../utils/toast.ts";
import { buildSignupPayload, type ContactStepValues, type PersonalStepValues } from "./schemas/create-employee.schema.ts";
import { usePrejoinOtp } from "../onboard-verify/hooks/use-prejoin-otp.ts";
import { VerifyEmailOtpModal } from "../onboard-verify/modals/verify-email-otp-modal.tsx";
import { useCreateEmployeeSignup } from "./hooks/use-create-employee-signup.ts";
import { PersonalStep } from "./steps/personal-step.tsx";
import { ContactStep } from "./steps/contact-step.tsx";
import { ReviewStep } from "./steps/review-step.tsx";

import styles from "./create-employee-wizard.module.css";

const STEPS = [
  { id: "personal", label: "Personal Details" },
  { id: "contact", label: "Contact Details" },
  { id: "review", label: "Review" },
] as const;

const SECTION_TITLES = ["Personal Details", "Contact Details", "Review"];

function emptyPersonal(): PersonalStepValues {
  return {
    firstName: "",
    middleName: "",
    lastName: "",
    personalEmail: "",
    gender: "",
    bloodGroup: "",
    dob: "",
    aadharNumber: "",
    panCardNumber: "",
    passportNumber: "",
    joiningDate: "",
    maritalStatus: "",
    anniversaryDate: "",
    spouseName: "",
    spouseDob: "",
    hasChild: undefined,
    childName: "",
    childDob: "",
    childGender: undefined,
  } as PersonalStepValues;
}

function emptyContact(): ContactStepValues {
  return {
    phoneNumber: "",
    alternateNumber: "",
    relationWithAlternateNo: "mother",
    alternateName: "",
    presentAddress: "",
    permanentAddress: "",
  };
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong. Please try again.";
}

export type CreateEmployeeWizardProps = {
  token: string;
  onSubmitted?: () => void;
};

export function CreateEmployeeWizard({ token, onSubmitted }: CreateEmployeeWizardProps) {
  const { signup } = useCreateEmployeeSignup();
  const { generateOtp, validateOtp } = usePrejoinOtp();

  const verification = useVerifiedOnboarding();
  const verifiedRecipient = verification?.token === token ? verification : null;
  const [emailVerified, setEmailVerified] = useState(Boolean(verifiedRecipient));
  const [personal, setPersonal] = useState<PersonalStepValues>(() => ({ ...emptyPersonal(), ...(verifiedRecipient ? { personalEmail: verifiedRecipient.email, firstName: verifiedRecipient.firstName ?? "", lastName: verifiedRecipient.lastName ?? "" } : {}) }));
  const [contact, setContact] = useState<ContactStepValues>(emptyContact);
  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onGenerateOtp = useCallback(
    (email: string) => generateOtp(token, email),
    [token, generateOtp],
  );

  const onValidate = useCallback(
    async (email: string, otp: string) => {
      await validateOtp(token, email, otp);
      setPersonal((p) => ({ ...p, personalEmail: email.trim() }));
      setEmailVerified(true);
    },
    [token, validateOtp],
  );

  const onPersonalNext = useCallback((v: PersonalStepValues) => {
    setPersonal(v);
    setStepIndex(1);
  }, []);

  const onContactNext = useCallback((v: ContactStepValues) => {
    setContact(v);
    setStepIndex(2);
  }, []);

  const onFinalSubmit = useCallback(async () => {
    setBusy(true);
    try {
      const payload = buildSignupPayload({ ...personal, ...contact }, token);
      await signup(payload);
      setSubmitted(true);
      onSubmitted?.();
      showSuccess("Your submission has been sent successfully.");
    } catch (e) {
      showError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [personal, contact, token, signup, onSubmitted]);

  if (!emailVerified) {
    return (
      <>
        <p className={`body-sm ${styles.verifyHint}`}>
          Verify your personal email in the dialog to continue.
        </p>
        <VerifyEmailOtpModal
          open={true}
          variant="personal"
          token={token}
          onGenerateOtp={onGenerateOtp}
          onValidate={onValidate}
        />
      </>
    );
  }

  if (submitted) {
    return (
      <Card variant="subtle" padding="5xl" className={styles.card}>
        <div className={styles.successMark} aria-hidden>
          <CircleCheck size={88} strokeWidth={1.35} />
        </div>
        <h2 className="heading-lg text-center">Thank you!</h2>
        <p className={`body-sm ${styles.lead} text-center`}>
          Your submission has been sent successfully.
          <br />
          HR will get back to you shortly.
        </p>
      </Card>
    );
  }

  const isLast = stepIndex === STEPS.length - 1;
  const formId = isLast ? undefined : `pre-step-${stepIndex}`;

  return (
    <div className={styles.wrap}>
      <WizardRailLayout
        steps={[...STEPS]}
        currentStepIndex={stepIndex}
        sectionTitle={SECTION_TITLES[stepIndex] ?? "Review"}
        onBack={() => setStepIndex((i) => Math.max(0, i - 1))}
        primaryLabel={isLast ? "Submit" : "Continue"}
        primaryLoading={busy}
        primaryFormId={formId}
        onPrimary={isLast ? () => void onFinalSubmit() : undefined}
      >
        {stepIndex === 0 ? (
          <PersonalStep
            key={personal.personalEmail}
            formId="pre-step-0"
            hideFooter
            defaultValues={personal}
            loading={busy}
            onNext={onPersonalNext}
          />
        ) : null}
        {stepIndex === 1 ? (
          <ContactStep
            formId="pre-step-1"
            hideFooter
            defaultValues={contact}
            loading={busy}
            onBack={() => setStepIndex(0)}
            onNext={onContactNext}
          />
        ) : null}
        {stepIndex === 2 ? (
          <ReviewStep
            hideFooter
            personal={personal}
            contact={contact}
            loading={busy}
            onBack={() => setStepIndex(1)}
            onSubmit={onFinalSubmit}
          />
        ) : null}
      </WizardRailLayout>
    </div>
  );
}
